import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';



import { useRecalledHere, useSetTestAnswer } from './answer-ref-context';
import { useBlockIdentity } from './fired-states';
import { reachableDrivenRows, standInForDrivenRow } from './recall-cycle';
import { DrivenRowStimulus } from './scenario-stimulus';
import { versionStructure } from './use-block-versions';
import { useDrivenRowInForce } from './varies-with-section';

import type { DrivenRow } from '@agavi/blocks';

import { IconButton } from '@/components/ui/icon-button';

/**
 * ONE ANSWER SETUP, for the scenario it answers — the scenario half of
 * `BranchConditionCarousel`.
 *
 * ## Why it is a box, and why the answers are inside it
 *
 * The first build put the scenario's name and pager in the ANSWER SETUP *heading*, beside the
 * label. That was wrong twice over: a branching block's control equation sits BELOW its heading
 * rather than inline with it, so the scenario was the one selector in the editor that read as
 * section furniture; and nothing drew the boundary of what the scenario governs. A box says it,
 * the same way the carousel's box says it for an equation: everything inside answers to the
 * selector at the top, and nothing outside does.
 *
 * ## The PROSE is not in here — `ScenarioStimulus` is
 *
 * It was, and it put a multi-paragraph stem between the question and the answers, so setting an
 * answer key meant scrolling back up to re-read the question on every row. The carousel's
 * arrangement was borrowed without the property that makes it work: an equation is one line.
 *
 * What separates them is authorship. The carousel's equation is AUTHORED in that box — it is a
 * control, so it belongs with the answer key. A scenario is read-only here; it is authored in
 * the leader. So the prose is CONTEXT and goes above the question, where the student meets it,
 * and what stays is the SELECTOR — which scenario am I setting up — plus the answers it governs.
 *
 * So this is deliberately the carousel's own shape — `rounded-lg border p-4`, a selector at the
 * top, navigation under it, the block's own payload as `children` — rather than a second visual
 * language for the same idea. A teacher meets both on one block.
 *
 * ## What it does NOT offer
 *
 * No add, no delete. The rows are a PROJECTION of the leader's variants (§4.1): one appears when
 * the leader gains a variant and goes when it loses one, and the only place to change that is
 * the leader. The carousel's `+`-on-the-last-step trick is exactly what must not be here.
 *
 * The name is read-only for the same reason — it is the leader's, shown so you know which
 * scenario you are answering, and renaming it belongs where it is authored.
 */
export function ScenarioRowFrame({
  scenarioId, name, stimulus, pager, unreviewed, unreviewedCount = 0, onDismissUnreviewed, children,
}: {
  /**
   * The row on screen, tagged for undo and the issues list.
   *
   * The carousel puts `data-field-id` on its active branch for this, and the scenario rows had
   * no equivalent — so an undo of an edit made on scenario 3 had nothing to aim at. Only one
   * row is mounted at a time, which is fine: `activeBranchIndex` is shared with the branch-jump
   * target, so a request to reveal a hidden row pages to it first.
   */
  scenarioId: string;
  /** The leader's own name for this variant. Always present — variants are born named (§8.4). */
  name: string | undefined;
  /**
   * THE ROW'S OWN CONTENT, where naming it is not enough — the driven axis.
   *
   * A scenario's variants are NAMED by their author, so a name is the right thing to show and
   * the prose sits above the box. A driven row is an ANSWER in another task, authored as an
   * option: a photograph, a sound, an equation or a chip that resolves per student, none of
   * which survives being flattened to stripped text. So that axis hands over a rendering and
   * this slot takes the name's place.
   */
  stimulus?: React.ReactNode;
  /**
   * The pager, supplied rather than built — a `VersionSelector` in browse mode.
   *
   * This frame owns the LAYOUT, the way `BranchConditionCarousel` owns its box and takes the
   * block's payload as `children`. Building a pager here would have been a third hand-rolled
   * chevron pair, and `VersionSelector`'s own doc is explicit that there must only be one: the
   * position label is the thing most likely to drift, and two pagers a few inches apart must
   * never disagree about which variant is on screen.
   */
  pager: React.ReactNode;
  unreviewed?: boolean;
  /**
   * How many rows are undecided ACROSS the bank, not just this one.
   *
   * The per-row strip below is where you act, and it was also the only place the flag appeared
   * — so a teacher who never paged to scenario 5 never learned scenario 5 was never decided,
   * which is the exact failure the flag exists to prevent, one level out.
   *
   * It cannot live in the block's issue list instead: `applyIssuePolicy` silences every
   * `incompletion` until the block is settled, deliberately, so that an unfinished thing does
   * not nag while it is being written. That is right for the publication gate and useless for
   * this. The count is reported there as well, for the gate; this is what makes it visible now.
   */
  unreviewedCount?: number;
  /** Clears the flag without editing anything — see the dismiss button's own note. */
  onDismissUnreviewed: () => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <div data-field-id={scenarioId} className='flex flex-col gap-3 rounded-lg border border-ws-border p-4'>
      {/* Centred over the box, like the pager below it — the two name the same thing, and the
          name left-aligned over a centred pager read as two unrelated rows.

          CONDITIONAL only on there BEING one. A driven row used to pass none, because what
          the box governed was showcased above it by `ScenarioStimulus` — and that echo is
          gone, as a duplicate of the Earlier answers row on the card. It was not a duplicate
          HERE: this box is what a teacher is designing the question and answer inside, and
          with nothing naming the row they were writing blind. Reported as "why is the
          scenario not shown in anymore when i'm trying to design the question/answer in the
          editor?". An empty `<p>` would still leave the box opening with a `gap-3` worth of
          nothing, so the guard stays. */}
      {stimulus ?? (name && <p className='text-center text-sm font-semibold text-ws-text'>{name}</p>)}

      {/* Navigation on its own row, centred — the carousel's placement, for its reason: the line
          above is variable-length content, and a pager sharing a row with it moves under the
          cursor every time the content changes.

          Also conditional, and for the same reason: a driven block pages in its QUESTION
          heading, so this row would be a second empty slab. */}
      {(pager || unreviewedCount > 0) && (
        <div className='flex items-center justify-center gap-2'>
          {pager}
          {unreviewedCount > 0 && (
            <span className='shrink-0 rounded-sm bg-ws-warning-bg px-1.5 py-0.5 text-[11px] font-medium text-ws-warning-fg'>
              {t('blocks.scenario.unreviewedCount', { count: unreviewedCount })}
            </span>
          )}
        </div>
      )}

      {/*
        * DISMISSIBLE, which is the point of it.
        *
        * The flag means "copied, not decided" (§4.4), and it clears itself when the row is
        * edited. But a row that is already right needs no edit — the copy IS the answer for this
        * scenario — and with no way to say so the warning would sit there permanently on every
        * scenario that happens to share an answer, which is how a warning stops being read.
        * Dismissing is the teacher saying "decided"; it is not an edit and changes nothing else.
        */}
      {unreviewed && (
        <div className='flex items-center gap-2 rounded-md bg-ws-warning-bg px-2 py-1'>
          <span className='flex-1 text-xs font-medium text-ws-warning-fg'>
            {t('blocks.scenario.unreviewed')}
          </span>
          <IconButton
            type='button'
            size='sm'
            aria-label={t('blocks.scenario.dismissUnreviewed')}
            onClick={onDismissUnreviewed}
          >
            <X className='h-4 w-4' />
          </IconButton>
        </div>
      )}

      {children}
    </div>
  );
}

/**
 * `ScenarioRowFrame` when the block follows something, and NOTHING when it does not.
 *
 * So an answer setup can be wrapped unconditionally at the call site. Numerical's is 660 lines
 * of JSX; a ternary around it would mean writing it twice or burying it in an IIFE, and the
 * only thing that actually varies is whether a box is drawn around it.
 */
export function ScenarioSetupFrame({ scenario, name, pager, unreviewedCount, onDismissUnreviewed, children }: {
  /** The row on screen, or undefined when this block follows nothing. */
  scenario: { scenarioId: string; unreviewed?: boolean } | undefined;
  name: string | undefined;
  pager: React.ReactNode;
  /** Across the whole bank — see `ScenarioRowFrame`. */
  unreviewedCount?: number;
  onDismissUnreviewed: () => void;
  children: React.ReactNode;
}) {
  if (!scenario) return <>{children}</>;
  return (
    <ScenarioRowFrame
      scenarioId={scenario.scenarioId}
      name={name}
      pager={pager}
      unreviewed={scenario.unreviewed}
      unreviewedCount={unreviewedCount}
      onDismissUnreviewed={onDismissUnreviewed}
    >
      {children}
    </ScenarioRowFrame>
  );
}


/**
 * THE SAME BOX, FOR A ROW AN EARLIER ANSWER SELECTED.
 *
 * A driven block had no box at all: the pager said "2 of 4" in the question heading and
 * nothing said which of the four was the Titan one, nothing marked a version as still a copy,
 * and nothing drew the boundary of what the row governs. Reported on review as "it seems to be
 * opening just the versions" — the versions were there and the infrastructure around them was
 * not.
 *
 * ## It encloses the QUESTION too, where a scenario's box does not
 *
 * That is the one real difference and it follows from the data. A scenario follower asks ONE
 * question across every variant, so its question is shared and sits above the box. A driven
 * block asks a DIFFERENT question per row — "how far on Mars" against "on Titan" — which is
 * the whole purpose of the axis, so the question belongs inside the row that owns it, and the
 * reviewed mark then genuinely covers everything in the box.
 *
 * ## It is NOT named, and it does NOT page
 *
 * Both are already said, once each, directly above it. `ScenarioStimulus` showcases the answer
 * the row was written for — which is an option and so may be a photograph, an equation or a
 * sound, none of which survives being flattened to a name — and the version pager stays in the
 * QUESTION heading where it has always been. Repeating either here put the same word in two
 * adjacent boxes and an empty row between them.
 *
 * ## Read from context, not passed
 *
 * Same arrangement as `VariesWithSection` and for the same reason: twelve blocks would
 * otherwise each compute the row, the label and the mark, which is the duplication that has
 * already produced a second link control and a stale warning in this feature alone.
 *
 * What it cannot read is the WRITE, so the three props are the three every block already has
 * to hand — the same shape `useDrivenVersionProjection` takes, so the call site is one line
 * that says nothing block-shaped. The alternative, a hand-written `onReviewed` closure per
 * block, is twelve copies of one `versions.map`.
 */
/**
 * ARROWS THAT STEP THE ANSWER, because the answer is the version.
 *
 * `VersionSelector` renders nothing on a driven block — a pager there would show a position it
 * could not change, and it was the control that DID move the question while the recall entry
 * moved only the text. That left the editor with no way forward at all: the only control was
 * the one on the CARD, so authoring a question and an answer key for each row meant leaving the
 * panel, clicking the card, and coming back. The owner: "i need to be able to cycle through the
 * answers with the arrow to update question text and correct answers".
 *
 * So this pages the DRIVER'S stand-in, which is the real pointer — the card's control, the box,
 * the question and the answer key all follow from it, as they already do when the card cycles.
 *
 * It wears `VersionSelector`'s own clothes down to the `IconButton size='sm'` pair around a
 * `text-xs font-medium tabular-nums` position, for the reason that component's doc gives: a
 * teacher meets both pagers on one block, and two invented separately read as two unrelated
 * widgets. It STOPS at the ends rather than wrapping, like that pager and unlike the card's
 * click-cycle — one press forward is a cycle, a pair of arrows is a position.
 */
function DrivenRowPager({ rows, currentRowId }: { rows: DrivenRow[]; currentRowId: string }) {
  const { t } = useTranslation();
  const identity = useBlockIdentity();
  const recalled = useRecalledHere(identity?.blockId);
  const setTestAnswer = useSetTestAnswer();
  const driver = recalled.find(r => r.driven);
  /*
   * THE ROWS, not the driver's answer choices — which is what makes "No answer" reachable.
   *
   * A blank row is the ABSENCE of an answer, so it is in no list of answers and both controls
   * paged past it: an MCQ with three options projected four versions and only three could be
   * opened. Walking the rows and asking `standInForDrivenRow` for each one puts every version
   * the projection made on the pager, in the driver's own order.
   *
   * Still filtered, because a keyword's catch-all has no honest stand-in — a control that
   * lands on a row it cannot select is worse than one that does not offer it.
   */
  const reachable = driver ? reachableDrivenRows(rows, driver.choices, driver.source) : [];
  if (!driver || !setTestAnswer || reachable.length < 2) return null;
  const index = Math.max(0, reachable.findIndex(r => r.id === currentRowId));
  const go = (to: number) => {
    const next = reachable[to] && standInForDrivenRow(reachable[to]!, driver.choices, driver.source);
    if (next) setTestAnswer(driver.aliasId, next.sample);
  };
  return (
    <div className='flex items-center gap-0.5'>
      <IconButton
        type='button' size='sm'
        aria-label={t('blocks.drivenVersions.rowPrevious')}
        disabled={index === 0}
        onClick={() => go(index - 1)}
      >
        <ChevronLeft className='h-4 w-4' />
      </IconButton>
      {/* `aria-live`, like every other pager here: paging IS the interaction, and a reader who
          cannot see the question change needs to hear which row they landed on. */}
      <span className='shrink-0 px-0.5 text-xs font-medium tabular-nums text-ws-muted' aria-live='polite'>
        {t('blocks.versions.positionShort', { current: index + 1, total: reachable.length })}
      </span>
      <IconButton
        type='button' size='sm'
        aria-label={t('blocks.drivenVersions.rowNext')}
        disabled={index >= reachable.length - 1}
        onClick={() => go(index + 1)}
      >
        <ChevronRight className='h-4 w-4' />
      </IconButton>
    </div>
  );
}

export function DrivenRowFrame<V extends { id: string; unreviewed?: boolean }>({
  block, data, emit, children,
}: {
  block: { data?: Record<string, unknown> };
  /** The block's own normalised data — `storedData` at every call site. */
  data: { versions?: V[] };
  emit: (next: Record<string, unknown>) => void;
  children: React.ReactNode;
}) {
  const row = useDrivenRowInForce();
  if (!row) return <>{children}</>;
  return (
    <ScenarioRowFrame
      scenarioId={row.versionId}
      name={undefined}
      /* WHICH ANSWER THIS BOX IS FOR, rendered rather than named — see the slot's own note and
         `DrivenRowStimulus`, which falls back to the label where the row has no content of its
         own and names `other`/`blank` in words, since "" and "anything else" are not names a
         teacher would recognise. */
      stimulus={<DrivenRowStimulus row={row.row} driverTaskId={row.driverTaskId} />}
      pager={<DrivenRowPager rows={row.rows} currentRowId={row.row.id} />}
      unreviewed={row.unreviewed}
      unreviewedCount={row.unreviewedCount}
      /* `versionStructure`, not the block's field writer: this replaces the version LIST, and
         a field writer folds its payload into the version in force — so the list would be
         written inside one of its own members. `useDrivenVersionProjection`'s own note. */
      onDismissUnreviewed={() => emit(versionStructure(block.data ?? {}, {
        versions: (data.versions ?? []).map(
          v => (v.id === row.versionId ? { ...v, unreviewed: undefined } : v)),
      }))}
    >
      {children}
    </ScenarioRowFrame>
  );
}
